import type { VariantProps } from 'class-variance-authority';

import type { buttonVariants } from './button-variants.js';

import * as React from 'react';

import { Button } from '#ui';
import { MaterialIcon } from '#ui/MaterialIcon.js';

export type CopyButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onCopy'> &
  VariantProps<typeof buttonVariants> & {
    content?: string;
    getContent?: () => string;
    delay?: number;
    onCopy?: (content: string) => void;
    isCopied?: boolean;
    onCopyChange?: (isCopied: boolean) => void;
    tooltip?: string;
  };
export function CopyButton({
  content,
  getContent,
  className,
  size,
  variant,
  delay = 3000,
  onClick,
  onCopy,
  isCopied,
  onCopyChange,
  tooltip,
  ...props
}: CopyButtonProps) {
  const [localIsCopied, setLocalIsCopied] = React.useState(isCopied ?? false);
  React.useEffect(() => {
    setLocalIsCopied(isCopied ?? false);
  }, [isCopied]);
  const handleIsCopied = React.useCallback(
    (isCopied: boolean) => {
      setLocalIsCopied(isCopied);
      onCopyChange?.(isCopied);
    },
    [onCopyChange],
  );
  const handleCopy = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isCopied) return;
      const text = content ?? getContent?.();
      if (text) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            handleIsCopied(true);
            setTimeout(() => handleIsCopied(false), delay);
            onCopy?.(text);
          })
          .catch((error) => {
            console.error('Error copying command', error);
          });
      }
      onClick?.(e);
    },
    [isCopied, content, getContent, onClick, handleIsCopied, delay, onCopy],
  );
  return (
    <Button variant={variant} size={size} className={className} onClick={handleCopy} tooltip={tooltip} {...props}>
      <MaterialIcon icon={localIsCopied ? 'check' : 'content_copy'} filled={localIsCopied} size={'xs'} />
    </Button>
  );
}
